import { clear as clearIndexedDB } from "idb-keyval";
import { remiLogger } from "@remi/logging";

import { queryClient } from "./api";

type ClearAllCacheProps = {
  clearQueryClient?: boolean;
  clearIndexedDb?: boolean;
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
};

const PRESERVED_LOCALSTORAGE_PREFIXES = ["TableView-v", "TableViewV2-v", "sidebar-", "views-sidebar-"] as const;

export const clearLocalStorageSelectively = () => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !PRESERVED_LOCALSTORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
};

/**
 * Clears all cached data on logout.
 * Handles errors gracefully to ensure logout always completes.
 */
export const clearAllCache = async ({
  clearQueryClient = true,
  clearIndexedDb = true,
  clearLocalStorage = true,
  clearSessionStorage = true,
}: ClearAllCacheProps = {}): Promise<void> => {
  // Clear React Query cache (in-memory + mutations)
  if (clearQueryClient) {
    try {
      queryClient.clear();
    } catch (error) {
      remiLogger.error("Failed to clear React Query cache:", { error });
    }
  }

  // Clear IndexedDB persistent cache
  if (clearIndexedDb) {
    try {
      await clearIndexedDB();
    } catch (error) {
      remiLogger.error("Failed to clear IndexedDB:", { error });
    }
  }

  // Clear localStorage (preserving table view and sidebar preferences)
  if (clearLocalStorage) {
    try {
      clearLocalStorageSelectively();
    } catch (error) {
      remiLogger.error("Failed to clear localStorage:", { error });
    }
  }

  // Clear sessionStorage
  if (clearSessionStorage) {
    try {
      sessionStorage.clear();
    } catch (error) {
      remiLogger.error("Failed to clear sessionStorage:", { error });
    }
  }
};
